import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { CSSProperties, forwardRef } from 'react';
import { CheckboxGroupProps } from '../Checkbox/types';
import { FormControlWrapper, FormLabelWrapper, HelperText, InfoTooltip } from '@shared/components';

const RadioGroupField = forwardRef(
  (
    {
      label,
      options,
      noOptionsText,
      error,
      onChange,
      onBlur,
      name,
      color,
      disabled,
      margin,
      fullWidth,
      required,
      direction = 'column',
      labelDirection = 'column',
      tooltipText,
      labelWidth = 100,
      size = 'medium',
      value,
    }: Omit<CheckboxGroupProps, 'value'> & { value: string },
    ref
  ) => {
    const inputId = `${name}-id`;
    const formLabelId = `${name}-label`;
    const helperTextId = `${name}-helperText`;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    };

    return (
      <FormControl
        component="fieldset"
        variant="standard"
        error={!!error}
        margin={margin}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
      >
        <FormControlWrapper labelDirection={labelDirection}>
          {label && (
            <FormLabelWrapper direction={labelDirection} width={labelWidth} size={size}>
              {typeof label === 'string' ? (
                <FormLabel htmlFor={inputId} id={formLabelId}>
                  {label}
                </FormLabel>
              ) : (
                label
              )}
              {tooltipText && <InfoTooltip tooltipText={tooltipText} disabled={disabled} />}
            </FormLabelWrapper>
          )}

          <RadioGroup id={inputId} name={name} value={value} onChange={handleChange}>
            {options.length > 0 ? (
              options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  label={option.label}
                  disabled={disabled || option.disabled}
                  control={<Radio size="small" value={option.value} style={radioStyle} />}
                  componentsProps={{ typography: { variant: 'body1' } }}
                  ref={ref}
                  id={`${option.value}-${formLabelId}`}
                />
              ))
            ) : (
              <Typography variant="body1">{noOptionsText}</Typography>
            )}
          </RadioGroup>

          {error && (
            <HelperText id={helperTextId} status="error">
              {error}
            </HelperText>
          )}
        </FormControlWrapper>
      </FormControl>
    );
  }
);

export default RadioGroupField;

const radioStyle: CSSProperties = {
  height: '32px',
  marginLeft: '2px',
};
